import React, { useEffect, useState } from 'react';

const MEDIA_BLOCKS = [
  { title: 'Square Promo 1', hint: 'Upload 1080 x 1080 image', tone: 'from-violet-500 to-purple-700' },
  { title: 'Square Promo 2', hint: 'Upload 1080 x 1080 image', tone: 'from-emerald-500 to-green-700' },
];

const SOLAR_PROMO_SLIDES = [
  { src: '/solar-promo-1.png', alt: 'Solar promotion 1' },
  { src: '/solar-promo-2.png', alt: 'Solar promotion 2' },
];

const VIDEO_SLOTS = [
  { title: 'Video Slot 1', hint: 'Upload MP4/WebM promo video' },
  { title: 'Video Slot 2', hint: 'Upload MP4/WebM promo video' },
  { title: 'Video Slot 3', hint: 'Upload MP4/WebM promo video' },
];

const BRAND_SLOTS = ['Brand Tile 1', 'Brand Tile 2', 'Brand Tile 3', 'Brand Tile 4', 'Brand Tile 5'];

function UploadBadge({ label }) {
  return (
    <span className="inline-flex rounded-full border border-white/30 bg-white/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white/95">
      {label}
    </span>
  );
}

export default function StorefrontMediaLayout({ onShopClick }) {
  const [solarIndex, setSolarIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setSolarIndex((i) => (i + 1) % SOLAR_PROMO_SLIDES.length);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="space-y-5 sm:space-y-7" aria-label="Storefront media layout">
      <div className="grid grid-cols-1 items-stretch gap-3 sm:gap-4 lg:grid-cols-12">
        <article className="space-y-4 lg:col-span-8">
          <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:rounded-2xl">
            <div className="relative aspect-[4/3] p-4 sm:aspect-[16/6] sm:p-7">
              <img
                src="/cctv-banner.png"
                alt="CCTV solutions banner"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-slate-950/60 via-slate-900/25 to-transparent" aria-hidden />
              <div className="relative z-10">
                <UploadBadge label="CCTV banner active" />
                <h3 className="mt-3 text-lg font-bold text-white drop-shadow sm:text-2xl">CCTV Solutions Banner</h3>
                <p className="mt-2 max-w-xl text-xs text-slate-100 drop-shadow sm:text-sm">
                  Main campaign slot now uses your uploaded CCTV image.
                </p>
              </div>
              <button
                type="button"
                onClick={onShopClick}
                className="relative z-10 mt-4 min-h-[44px] rounded-lg bg-white px-4 py-2 text-xs font-bold uppercase tracking-wide text-slate-900 transition hover:bg-slate-100 sm:min-h-0"
              >
                Shop now
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {MEDIA_BLOCKS.map((block) => (
            <article key={block.title} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:rounded-2xl">
              <div className={`aspect-square bg-gradient-to-br p-3 sm:aspect-[6/5] sm:p-4 ${block.tone}`}>
                <UploadBadge label={block.hint} />
                <p className="mt-3 text-xs font-bold text-white sm:text-base">{block.title}</p>
              </div>
            </article>
            ))}
          </div>
        </article>
        <article className="lg:col-span-4">
          <div className="relative aspect-[858/1834] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:rounded-2xl">
            {SOLAR_PROMO_SLIDES.map((slide, i) => (
              <img
                key={slide.src}
                src={slide.src}
                alt={slide.alt}
                className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-700 ${
                  i === solarIndex ? 'opacity-100' : 'pointer-events-none opacity-0'
                }`}
                loading="lazy"
                decoding="async"
              />
            ))}
            <div className="absolute inset-x-0 bottom-3 z-10 flex justify-center px-4">
              <div className="flex items-center gap-1.5 rounded-full bg-slate-950/45 px-2 py-1 backdrop-blur-sm">
                {SOLAR_PROMO_SLIDES.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSolarIndex(i)}
                    className="flex h-6 w-6 items-center justify-center"
                    aria-label={`Show solar promo ${i + 1}`}
                  >
                    <span
                      className={`h-2 rounded-full transition-all ${i === solarIndex ? 'w-5 bg-white' : 'w-2 bg-white/60'}`}
                      aria-hidden
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </article>
      </div>

      <div className="space-y-3">
        <div className="flex items-end justify-between">
          <h3 className="text-base font-extrabold uppercase tracking-wide text-slate-900 sm:text-lg">Videos & Promos</h3>
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Layout only</span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {VIDEO_SLOTS.map((slot) => (
            <article key={slot.title} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex aspect-video items-center justify-center bg-slate-900/95 p-4">
                <div className="text-center">
                  <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white">▶</div>
                  <p className="mt-3 text-sm font-bold text-white">{slot.title}</p>
                  <p className="mt-1 text-[11px] text-slate-300">{slot.hint}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <article className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:rounded-2xl lg:col-span-8">
          <div className="aspect-[4/3] bg-gradient-to-r from-[#5c2d91] via-[#6f3fb1] to-[#8cc63f] p-4 sm:aspect-[16/5] sm:p-7">
            <UploadBadge label="Upload campaign strip image" />
            <h3 className="mt-3 text-lg font-bold text-white sm:text-2xl">Deals & Promotions Banner Strip</h3>
            <p className="mt-2 text-xs text-white/90 sm:text-sm">Use this area for rotating sale strips and campaign announcements.</p>
          </div>
        </article>
        <article className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:rounded-2xl lg:col-span-4">
          <div className="aspect-[4/3] bg-gradient-to-br from-amber-500 to-orange-700 p-4 sm:aspect-[6/5]">
            <UploadBadge label="Upload social post image" />
            <p className="mt-3 text-sm font-bold text-white sm:text-base">Social / Featured Collection Card</p>
          </div>
        </article>
      </div>

      <div className="space-y-3">
        <h3 className="text-base font-extrabold uppercase tracking-wide text-slate-900 sm:text-lg">Top Brands Layout</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {BRAND_SLOTS.map((brand) => (
            <article key={brand} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="flex aspect-[5/3] items-center justify-center bg-slate-100 px-3 text-center text-xs font-bold uppercase tracking-wide text-slate-600">
                {brand}
              </div>
            </article>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-[#5c2d91]">Testimonials Area</p>
          <p className="mt-2 text-sm text-slate-700">Reserve this card for customer feedback blocks like the reference layout.</p>
        </article>
        <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-[#5c2d91]">Collection Highlight</p>
          <p className="mt-2 text-sm text-slate-700">Reserve this card for themed collection sections (Gaming, Pink Build, etc.).</p>
        </article>
      </div>
    </section>
  );
}
